import React, { useState, useEffect } from 'react';
import { BookOpen, MapPin, X, ChevronDown, ChevronRight, UserPlus, GraduationCap, Maximize2, Minimize2, Pencil, Trash2, Mail, BarChart2, Filter, Search, FileText, CheckCircle, AlertCircle, Clock, Download, Printer } from 'lucide-react';
import api from '../api';
import CreateDiplomaturaMenu from './CreateDiplomaturaMenu';
import CreateMateriaMenu from './CreateMateriaMenu';
import AuthorityEnrollmentModal from './AuthorityEnrollmentModal';
import CustomCertPrintModal from './CustomCertPrintModal';

const DiplomaturasListModal = ({ onClose, activeRole }) => {
  const [expandedDiplo, setExpandedDiplo] = useState(null);
  const [expandedMateria, setExpandedMateria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [diplomaturas, setDiplomaturas] = useState([]);
  const [editDiplo, setEditDiplo] = useState(null);
  const [editMateria, setEditMateria] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [materiaStudents, setMateriaStudents] = useState({});
  const [materiaLoading, setMateriaLoading] = useState({});
  const [activeSubmenus, setActiveSubmenus] = useState({});
  const [attendanceReports, setAttendanceReports] = useState({});
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [selectedDiploForEnroll, setSelectedDiploForEnroll] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Alumnos Vigentes (descarga con criterio de % de asistencia configurable)
  const [showActiveStudentsModal, setShowActiveStudentsModal] = useState(false);
  const [selectedDiploForActiveStudents, setSelectedDiploForActiveStudents] = useState(null);
  const [activeStudentsThreshold, setActiveStudentsThreshold] = useState(60);
  const [isDownloadingActiveStudents, setIsDownloadingActiveStudents] = useState(false);

  // Certificate Request States
  const [certificateRequests, setCertificateRequests] = useState([]);
  const [showCertificateRequestModal, setShowCertificateRequestModal] = useState(false);
  const [selectedDiploForCertificate, setSelectedDiploForCertificate] = useState(null);
  const [requestMunicipality, setRequestMunicipality] = useState("");
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [certType, setCertType] = useState(null); // null | 'regular' | 'personalizada'
  const [customDescription, setCustomDescription] = useState("");
  const [isSubmittingCustom, setIsSubmittingCustom] = useState(false);
  const [myCustomCertRequests, setMyCustomCertRequests] = useState([]);
  const [showCustomCertView, setShowCustomCertView] = useState(false);
  const [customCertToView, setCustomCertToView] = useState(null);

  useEffect(() => {
    if (!activeRole) return;
    fetchDiplomaturas();
    if (activeRole === 'AL' || activeRole === 'ACO' || activeRole === 'CD') {
      fetchCertificateRequests();
    }
    if (activeRole === 'AL') {
      api.get('users/profile/').then(res => setUserProfile(res.data)).catch(() => {});
      api.get('custom-certificate-requests/').then(res => setMyCustomCertRequests(res.data)).catch(() => {});
    }
  }, [activeRole]);

  const fetchCertificateRequests = async () => {
    try {
      const res = await api.get('certificate-requests/');
      setCertificateRequests(res.data);
    } catch (err) {
      console.error("Error fetching certificate requests:", err);
    }
  };

  const profileNameMissing = userProfile && (!(userProfile.first_name || '').trim() || !(userProfile.last_name || '').trim());

  const handleOpenCertificateModal = (diplo) => {
    setSelectedDiploForCertificate(diplo);
    setRequestMunicipality(diplo.municipio || "");
    setCertType(null);
    setCustomDescription("");
    setShowCertificateRequestModal(true);
  };

  const handleRequestCustomCertificate = async () => {
    if (!customDescription.trim()) {
      alert("Por favor describí brevemente para qué necesitás la constancia.");
      return;
    }
    setIsSubmittingCustom(true);
    try {
      await api.post('custom-certificate-requests/', {
        diploma: selectedDiploForCertificate.id,
        student_description: customDescription,
      });
      const res = await api.get('custom-certificate-requests/');
      setMyCustomCertRequests(res.data);
      setShowCertificateRequestModal(false);
      setCustomDescription("");
    } catch (err) {
      console.error(err);
      alert("Error al enviar la solicitud.");
    } finally {
      setIsSubmittingCustom(false);
    }
  };

  const handleRequestCertificate = async () => {
    if (!requestMunicipality) {
      alert("Por favor, ingresa el municipio.");
      return;
    }
    if (userProfile && (!(userProfile.first_name || '').trim() || !(userProfile.last_name || '').trim())) {
      alert("Debes completar tu nombre y apellido en el perfil antes de solicitar una constancia.");
      return;
    }

    setIsSubmittingRequest(true);
    try {
      await api.post('certificate-requests/', {
        diploma: selectedDiploForCertificate.id,
        municipality: requestMunicipality
      });
      alert("Solicitud enviada con éxito. El coordinador ha sido notificado.");
      setShowCertificateRequestModal(false);
      setRequestMunicipality("");
      fetchCertificateRequests();
    } catch (err) {
      console.error("Error creating certificate request:", err);
      const errMsg = err.response?.data?.error?.[0] || err.response?.data?.detail || "Error al enviar la solicitud.";
      alert(errMsg);
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Intentar obtener el email de varias fuentes para mayor seguridad
  const userEmail = (localStorage.getItem('userEmail') || sessionStorage.getItem('userEmail') || '').toLowerCase().trim();

  const userCoordinatedDiplos = JSON.parse(sessionStorage.getItem('userCoordinatedDiplos')) || [];

  const fetchDiplomaturas = async () => {
    setLoading(true);
    try {
      // If student, only fetch enrolled diplomas
      const params = activeRole === 'AL' ? '?enrolled=true' : '';
      const res = await api.get(`diplomas/${params}`);
      setDiplomaturas(res.data);
    } catch (err) {
      console.error('Error fetching diplomaturas:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleMateria = async (id) => {
    if (expandedMateria === id) {
      setExpandedMateria(null);
    } else {
      setExpandedMateria(id);
      if (!activeSubmenus[id]) {
        setActiveSubmenus(prev => ({ ...prev, [id]: 'datos' }));
      }
      
      if (!materiaStudents[id]) {
        fetchSubjectData(id);
      }
    }
  };

  const fetchSubjectData = async (id) => {
    setMateriaLoading(prev => ({ ...prev, [id]: true }));
    try {
      const [studentsRes, attendanceRes] = await Promise.all([
        api.get(`subjects/${id}/students/`),
        api.get(`subjects/${id}/attendance-report/`)
      ]);
      setMateriaStudents(prev => ({ ...prev, [id]: studentsRes.data }));
      setAttendanceReports(prev => ({ ...prev, [id]: attendanceRes.data }));
    } catch (err) {
      console.error('Error fetching subject data:', err);
    } finally {
      setMateriaLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleSubmenuChange = (materiaId, submenu) => {
    setActiveSubmenus(prev => ({ ...prev, [materiaId]: submenu }));
  };

  const toggleDiplo = (id) => {
    if (expandedDiplo === id) {
      setExpandedDiplo(null);
      setExpandedMateria(null);
    } else {
      setExpandedDiplo(id);
      setExpandedMateria(null);
    }
  };


  const handleDownloadAttendancePdf = async (diplo) => {
    try {
      const res = await api.get(`diplomas/${diplo.id}/attendance_pdf/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0,10).replace(/-/g,'');
      link.setAttribute('download', `asistencias_${diplo.municipio?.toLowerCase().replace(/\s+/g,'_') || diplo.id}_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando PDF:', err);
      alert('Error al generar el PDF de asistencias.');
    }
  };

  const handleOpenActiveStudentsModal = (diplo) => {
    setSelectedDiploForActiveStudents(diplo);
    setActiveStudentsThreshold(60);
    setShowActiveStudentsModal(true);
  };

  const handleDownloadActiveStudents = async () => {
    const diplo = selectedDiploForActiveStudents;
    if (!diplo) return;
    setIsDownloadingActiveStudents(true);
    try {
      const res = await api.get(`diplomas/${diplo.id}/active-students/`, {
        params: { min_pct: activeStudentsThreshold },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0,10).replace(/-/g,'');
      link.setAttribute('download', `alumnos_vigentes_${activeStudentsThreshold}pct_${diplo.municipio?.toLowerCase().replace(/\s+/g,'_') || diplo.id}_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowActiveStudentsModal(false);
    } catch (err) {
      console.error('Error descargando alumnos vigentes:', err);
      alert('Error al generar el listado de alumnos vigentes.');
    } finally {
      setIsDownloadingActiveStudents(false);
    }
  };

  const handleDeleteDiplo = async (diplo) => {
    const confirmed = window.confirm(`¿Está seguro que desea eliminar la diplomatura "${diplo.name}"? Esta acción no se puede deshacer y eliminará todas las materias asociadas.`);
    if (!confirmed) return;

    try {
      await api.delete(`diplomas/${diplo.id}/`);
      alert(`Diplomatura "${diplo.name}" eliminada.`);
      fetchDiplomaturas();
    } catch (err) {
      console.error('Error deleting diplomatura:', err);
      alert('Error al intentar eliminar la diplomatura.');
    }
  };

  return (
    <>
    <div className={`modal show d-block ${isExpanded ? 'p-0' : ''}`} tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className={`modal-dialog modal-dialog-centered ${isExpanded ? 'm-0 mw-100' : 'modal-lg'}`}>
        <div className={`modal-content glassmorphism ${isExpanded ? 'modal-expanded' : ''}`}>
          <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="modal-title section-title fs-4 mb-0 d-flex align-items-center">
              <BookOpen size={24} className="me-2 text-info" /> Diplomaturas Vigentes
            </h5>
            <div className="d-flex gap-2 align-items-center">
                <button 
                  type="button" 
                  onClick={() => setIsExpanded(!isExpanded)} 
                  className="btn-glass p-1 d-flex align-items-center justify-content-center"
                  title={isExpanded ? "Contraer" : "Ampliar"}
                  style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                >
                  {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
                <button
                type="button"
                className="btn-close-premium"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>
          <div className="modal-body">
            <p className="text-muted mb-4">
              {activeRole === 'AL'
                ? 'Listado de las diplomaturas en las que estás inscripto y tu rendimiento académico.'
                : (activeRole === 'DO' || activeRole === 'PR')
                  ? 'Listado de sus materias asignadas y el rendimiento de sus alumnos.' 
                  : 'Listado general de diplomaturas y sus municipios correspondientes.'}
            </p>
            
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
            )}
            <div className="d-flex flex-column gap-3">
              {!loading && diplomaturas.map((diplo) => {
                const diploMaterias = diplo.subjects || [];
                const isDiploExpanded = expandedDiplo === diplo.id;

                return (
                  <div key={diplo.id} className="card bg-transparent border-secondary border-opacity-25 shadow-sm">
                    {/* Botón de la Diplomatura */}
                    <div className="d-flex align-items-center">
                      <button 
                        className={`btn flex-grow-1 text-start p-3 d-flex justify-content-between align-items-center ${isDiploExpanded ? 'bg-primary bg-opacity-10' : ''}`}
                        onClick={() => toggleDiplo(diplo.id)}
                        style={{ border: 'none' }}
                      >
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h6 className={`fw-bold mb-0 ${isDiploExpanded ? 'text-primary' : 'text-dark'}`}>{diplo.name}</h6>
                            {activeRole === 'ACO' && (
                              <Pencil 
                                size={14} 
                                className="text-muted cursor-pointer opacity-50 hover-opacity-100" 
                                onClick={(e) => { e.stopPropagation(); setEditDiplo(diplo); }}
                                title="Renombrar Diplomatura"
                              />
                            )}
                          </div>
                          <div className="d-flex flex-column text-muted small">
                            <div className="d-flex align-items-center">
                              <MapPin size={14} className="me-1" />
                              {diplo.municipio}
                            </div>
                            {diplo.referentes_data && diplo.referentes_data.length > 0 && (
                              <div className="d-flex align-items-center mt-1 flex-wrap gap-1">
                                <UserPlus size={14} className="me-1 text-primary" />
                                <span className="fw-medium text-uppercase x-small me-1">Referente{diplo.referentes_data.length > 1 ? 's' : ''}:</span> 
                                {diplo.referentes_data.map(r => r.name || r.email).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          {activeRole !== 'AL' && diplo.dropout_rate !== undefined && diplo.dropout_rate !== null && (
                            <div className="d-flex align-items-center bg-white rounded shadow-sm px-2 py-1 border border-danger border-opacity-25" title="Grado de Deserción">
                              <BarChart2 size={14} className="text-danger me-1" />
                              <span className="small fw-bold text-danger text-uppercase me-2 d-none d-sm-inline" style={{fontSize: '0.65rem'}}>Grado de Deserción:</span>
                              <span className="badge bg-danger rounded-pill">{diplo.dropout_rate}%</span>
                            </div>
                          )}
                          {activeRole !== 'AL' && (
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm rounded-pill px-2 py-1 d-flex align-items-center gap-1 shadow-sm"
                              style={{ fontSize: '0.65rem' }}
                              title="Descargar alumnos vigentes (elegir % de asistencia)"
                              onClick={(e) => { e.stopPropagation(); handleOpenActiveStudentsModal(diplo); }}
                            >
                              <Download size={14} />
                              <span className="fw-bold d-none d-sm-inline">VIGENTES</span>
                            </button>
                          )}
                          {activeRole === 'AL' && (
                            <div className="d-flex align-items-center me-2">
                          {activeRole === 'AL' && (() => {
                            const req = certificateRequests.find(r => r.diploma === diplo.id);
                            if (!req) {
                              return (
                                <button
                                  className="btn btn-success btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 shadow-sm border-0"
                                  onClick={(e) => { e.stopPropagation(); handleOpenCertificateModal(diplo); }}
                                  style={{ fontSize: '0.7rem' }}
                                >
                                  <FileText size={14} />
                                  <span className="fw-bold d-none d-sm-inline">CONSTANCIA</span>
                                </button>
                              );
                            }

                            if (req.status === 'PE' || req.status === 'VC') {
                              return (
                                <div className="d-flex align-items-center gap-1 text-warning px-2 py-1 bg-warning bg-opacity-10 rounded border border-warning border-opacity-25 shadow-sm">
                                  <Clock size={12} />
                                  <span className="fw-bold x-small text-uppercase" style={{fontSize: '0.6rem'}}>
                                    {req.status === 'VC' ? 'Validada' : 'Pendiente'}
                                  </span>
                                </div>
                              );
                            }

                            if (req.status === 'RE') {
                              return (
                                <div className="d-flex flex-column align-items-end">
                                  <div className="d-flex align-items-center gap-1 text-danger px-2 py-1 bg-danger bg-opacity-10 rounded border border-danger border-opacity-25 shadow-sm mb-1">
                                    <AlertCircle size={12} />
                                    <span className="fw-bold x-small text-uppercase" style={{fontSize: '0.6rem'}}>Rechazada</span>
                                  </div>
                                  <button 
                                    className="btn btn-link p-0 text-success fw-bold x-small text-uppercase text-decoration-none"
                                    onClick={(e) => { e.stopPropagation(); handleOpenCertificateModal(diplo); }}
                                    style={{fontSize: '0.65rem'}}
                                  >
                                    Reintentar
                                  </button>
                                </div>
                              );
                            }

                            if (req.status === 'AP') {
                              return (
                                <div className="d-flex align-items-center gap-1">
                                  <button
                                    className="btn btn-primary btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 shadow-sm border-0"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const res = await api.get(`certificate-requests/${req.id}/download/`, { responseType: 'blob' });
                                        const blob = new Blob([res.data], { type: 'application/pdf' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `constancia_${req.id}.pdf`;
                                        a.click();
                                        URL.revokeObjectURL(url);
                                      } catch {
                                        alert('Error al descargar la constancia.');
                                      }
                                    }}
                                    style={{ fontSize: '0.7rem' }}
                                  >
                                    <FileText size={14} />
                                    <span className="fw-bold d-none d-sm-inline">DESCARGAR</span>
                                  </button>
                                  <button
                                    className="btn btn-success btn-sm rounded-pill px-3 py-1 d-flex align-items-center gap-2 shadow-sm border-0"
                                    onClick={(e) => { e.stopPropagation(); handleOpenCertificateModal(diplo); }}
                                    style={{ fontSize: '0.7rem' }}
                                    title="Solicitar nueva constancia"
                                  >
                                    <FileText size={14} />
                                    <span className="fw-bold d-none d-sm-inline">NUEVA</span>
                                  </button>
                                </div>
                              );
                            }

                            return null;
                          })()}

                          {/* Constancia Personalizada — estado directo en la lista */}
                          {activeRole === 'AL' && (() => {
                            // eslint-disable-next-line eqeqeq
                            const customReq = myCustomCertRequests.find(r => r.diploma == diplo.id);
                            if (!customReq) return null;

                            if (customReq.status === 'EM') {
                              return (
                                <button
                                  className="btn btn-outline-primary btn-sm rounded-pill px-2 py-1 d-flex align-items-center gap-1 shadow-sm ms-1"
                                  onClick={(e) => { e.stopPropagation(); setCustomCertToView(customReq); setShowCustomCertView(true); }}
                                  style={{ fontSize: '0.7rem' }}
                                  title="Ver constancia personalizada emitida"
                                >
                                  <Printer size={13} />
                                  <span className="fw-bold d-none d-sm-inline">PERSONALIZADA</span>
                                </button>
                              );
                            }

                            if (customReq.status === 'PE' || customReq.status === 'VC') {
                              return (
                                <div
                                  className="d-flex align-items-center gap-1 px-2 py-1 rounded ms-1"
                                  style={{ fontSize: '0.6rem', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)' }}
                                  title="Constancia personalizada en proceso"
                                >
                                  <Clock size={11} className="text-warning" />
                                  <span className="fw-bold text-warning text-uppercase">Personalizada solicitada</span>
                                </div>
                              );
                            }

                            return null;
                          })()}
                            </div>
                          )}
                          {isDiploExpanded ? <ChevronDown size={20} className="text-primary" /> : <ChevronRight size={20} className="text-muted" />}
                        </div>
                      </button>

                      {/* Botones de acción para ACO y CD (Mantener afuera para no interferir con el toggle) */}
                      {activeRole === 'ACO' && (
                        <div className="d-flex border-start border-secondary border-opacity-25">
                          <button 
                            className="btn btn-outline-primary border-0 p-3" 
                            onClick={() => {
                              setSelectedDiploForEnroll(diplo);
                              setShowEnrollmentModal(true);
                            }}
                            title="Inscribir Alumnos"
                          >
                            <UserPlus size={18} />
                          </button>
                          <button 
                            className="btn btn-outline-primary border-0 p-3" 
                            onClick={() => setEditDiplo(diplo)}
                            title="Editar Diplomatura"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            className="btn btn-outline-danger border-0 p-3"
                            onClick={() => handleDeleteDiplo(diplo)}
                            title="Eliminar Diplomatura"
                          >
                            <Trash2 size={18} />
                          </button>
                          <button
                            className="btn btn-outline-success border-0 p-3"
                            title="Descargar PDF de Asistencias"
                            onClick={() => handleDownloadAttendancePdf(diplo)}
                          >
                            <Download size={18} />
                          </button>
                        </div>
                      )}

                      {activeRole === 'CD' && diplo.coordinator_email === userEmail && (
                        <div className="d-flex border-start border-secondary border-opacity-25">
                          <button
                            className="btn btn-outline-primary border-0 p-3"
                            onClick={() => {
                              setSelectedDiploForEnroll(diplo);
                              setShowEnrollmentModal(true);
                            }}
                            title="Inscribir Alumnos"
                          >
                            <UserPlus size={18} />
                          </button>
                          <button
                            className="btn btn-outline-success border-0 p-3"
                            title="Descargar PDF de Asistencias"
                            onClick={() => handleDownloadAttendancePdf(diplo)}
                          >
                            <Download size={18} />
                          </button>
                        </div>
                      )}

                      {['CO', 'DO', 'RM'].includes(activeRole) && (
                        <div className="d-flex border-start border-secondary border-opacity-25">
                          <button
                            className="btn btn-outline-success border-0 p-3"
                            title="Descargar PDF de Asistencias"
                            onClick={() => handleDownloadAttendancePdf(diplo)}
                          >
                            <Download size={18} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Contenido Expandido: Materias */}
                    {isDiploExpanded && (
                      <div className="p-3 border-top border-secondary border-opacity-25 bg-light">
                        <h6 className="small fw-bold text-muted mb-3 d-flex align-items-center">
                          <BookOpen size={16} className="me-2 text-warning" /> MATERIAS ({diploMaterias.length})
                        </h6>
                        
                        {diploMaterias.length > 0 ? (
                          <div className="d-flex flex-column gap-2 ps-3 border-start border-warning border-3">
                             {diploMaterias.map(materia => {
                               const isMateriaExpanded = expandedMateria === materia.id;
                               const students = materiaStudents[materia.id] || [];
                               const isLoadingStudents = materiaLoading[materia.id];

                               return (
                                <div key={materia.id} className="card border-0 shadow-sm overflow-hidden">
                                  {/* Botón de la Materia */}
                                  <button 
                                    className={`btn w-100 text-start p-2 d-flex justify-content-between align-items-center ${isMateriaExpanded ? 'bg-warning bg-opacity-10' : 'bg-white'}`}
                                    onClick={() => toggleMateria(materia.id)}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="fw-medium text-dark">{materia.name}</span>
                                      {(activeRole === 'ACO' || (activeRole === 'CD' && diplo.coordinator_email === userEmail)) && (
                                        <Pencil 
                                          size={14} 
                                          className="text-muted cursor-pointer opacity-50 hover-opacity-100" 
                                          onClick={(e) => { e.stopPropagation(); setEditMateria(materia); }}
                                          title="Renombrar Materia"
                                        />
                                      )}
                                    </div>
                                    {isMateriaExpanded ? <ChevronDown size={18} className="text-warning" /> : <ChevronRight size={18} className="text-muted" />}
                                  </button>

                                  {/* Contenido Expandido: Submenús */}
                                  {isMateriaExpanded && (
                                    <div className="p-0 bg-white border-top">
                                      {/* Navegación de Submenús */}
                                      <div className="d-flex border-bottom bg-light px-3 overflow-x-auto">
                                        <button 
                                          className={`btn btn-sm py-2 px-3 border-0 rounded-0 fw-bold small ${activeSubmenus[materia.id] === 'datos' ? 'text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                          onClick={() => handleSubmenuChange(materia.id, 'datos')}
                                        >
                                          {activeRole === 'AL' ? 'MIS DATOS' : 'DATOS ALUMNOS'}
                                        </button>
                                        <button 
                                          className={`btn btn-sm py-2 px-3 border-0 rounded-0 fw-bold small ${activeSubmenus[materia.id] === 'notas' ? 'text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                          onClick={() => handleSubmenuChange(materia.id, 'notas')}
                                        >
                                          {activeRole === 'AL' ? 'MIS CALIFICACIONES' : 'CALIFICACIONES'}
                                        </button>
                                        <button 
                                          className={`btn btn-sm py-2 px-3 border-0 rounded-0 fw-bold small ${activeSubmenus[materia.id] === 'asistencia' ? 'text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                          onClick={() => handleSubmenuChange(materia.id, 'asistencia')}
                                        >
                                          {activeRole === 'AL' ? 'MI ASISTENCIA' : 'ASISTENCIAS'}
                                        </button>
                                        <button 
                                          className={`btn btn-sm py-2 px-3 border-0 rounded-0 fw-bold small ${activeSubmenus[materia.id] === 'profesores' ? 'text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                          onClick={() => handleSubmenuChange(materia.id, 'profesores')}
                                        >
                                          PROFESORES
                                        </button>
                                        <button 
                                          className={`btn btn-sm py-2 px-3 border-0 rounded-0 fw-bold small ${activeSubmenus[materia.id] === 'coordinador' ? 'text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                          onClick={() => handleSubmenuChange(materia.id, 'coordinador')}
                                        >
                                          COORDINADOR
                                        </button>
                                      </div>

                                      <div className="p-3">
                                        {isLoadingStudents ? (
                                          <div className="text-center p-4">
                                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                              <span className="visually-hidden">Cargando...</span>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="animate-fade-in">
                                            {/* Buscador Universal para la Materia */}
                                            {['datos', 'notas', 'asistencia'].includes(activeSubmenus[materia.id]) && (
                                              <div className="p-3 bg-light border-bottom">
                                                <div className="input-group input-group-sm shadow-sm" style={{ maxWidth: '400px' }}>
                                                  <span className="input-group-text bg-white border-end-0">
                                                    <Search size={14} className="text-primary" />
                                                  </span>
                                                  <input 
                                                    type="text" 
                                                    className="form-control border-start-0 ps-0" 
                                                    placeholder="Buscar alumno en esta materia..." 
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                  />
                                                  {searchTerm && (
                                                    <button 
                                                      className="btn btn-outline-secondary border-start-0 bg-white" 
                                                      onClick={() => setSearchTerm('')}
                                                    >
                                                      <X size={12} />
                                                    </button>
                                                  )}
                                                </div>
                                              </div>
                                            )}

                                              {/* VISTA 1: DATOS PERSONALES */}
                                            {activeSubmenus[materia.id] === 'datos' && (
                                              <div className="table-responsive">
                                                <table className="table table-sm table-hover premium-table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                                  <thead className="table-light text-muted">
                                                    <tr>
                                                      <th>Alumno</th>
                                                      <th>DNI</th>
                                                      <th>Email</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {students.filter(s => {
                                                      const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                      const studentEmail = (s.email || '').toLowerCase().trim();
                                                      const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                      return matchesSearch && isSelf;
                                                    }).length > 0 ? 
                                                      students.filter(s => {
                                                        const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                        const studentEmail = (s.email || '').toLowerCase().trim();
                                                        const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                        return matchesSearch && isSelf;
                                                      }).map(student => (
                                                        <tr key={student.id}>
                                                          <td className="fw-bold text-dark">{student.full_name}</td>
                                                          <td className="text-muted">{student.dni || 'N/A'}</td>
                                                          <td className="text-muted">{student.email}</td>
                                                        </tr>
                                                      )) : (
                                                        <tr><td colSpan="3" className="text-center p-3 text-muted fst-italic">
                                                          {activeRole === 'AL' ? `No se encontraron tus datos personales (${userEmail}).` : 'No se encontraron alumnos.'}
                                                        </td></tr>
                                                      )}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}

                                            {/* VISTA 2: CALIFICACIONES */}
                                            {activeSubmenus[materia.id] === 'notas' && (
                                              <div className="table-responsive">
                                                <table className="table table-sm table-hover premium-table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                                  <thead className="table-light text-muted">
                                                    <tr>
                                                      <th>Alumno</th>
                                                      <th className="text-center">Última Nota</th>
                                                      <th className="text-center">Promedio</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                     {students.filter(s => {
                                                       const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                       const studentEmail = (s.email || '').toLowerCase().trim();
                                                       const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                       return matchesSearch && isSelf;
                                                     }).length > 0 ? 
                                                      students.filter(s => {
                                                        const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                        const studentEmail = (s.email || '').toLowerCase().trim();
                                                        const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                        return matchesSearch && isSelf;
                                                      }).map(student => (
                                                        <tr key={student.id}>
                                                          <td className="fw-bold text-dark">{student.full_name}</td>
                                                          <td className="text-center">
                                                             {student.grade ? (
                                                               <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2">
                                                                 {student.grade}
                                                               </span>
                                                             ) : '-'}
                                                          </td>
                                                          <td className="text-center text-primary fw-bold">
                                                            {student.average || '-'} <GraduationCap size={14} className="ms-1 opacity-50"/>
                                                          </td>
                                                        </tr>
                                                      )) : (
                                                        <tr><td colSpan="3" className="text-center p-3 text-muted fst-italic">No se encontraron tus calificaciones.</td></tr>
                                                      )}
                                                  </tbody>
                                                </table>
                                              </div>
                                             )}
                                               {/* VISTA 3: MATRIZ DE ASISTENCIA */}
                                             {activeSubmenus[materia.id] === 'asistencia' && (
                                               <div className="animate-fade-in">
                                                 <div className="table-responsive overflow-x-auto shadow-sm rounded-3 border">
                                                   <table className="table table-sm table-bordered premium-table align-middle mb-0" style={{ fontSize: '0.75rem', minWidth: '600px' }}>
                                                     <thead>
                                                       <tr className="bg-light">
                                                         <th className="sticky-column bg-light" style={{ minWidth: '150px', zIndex: 1, position: 'sticky', left: 0 }}>Alumno</th>
                                                         {attendanceReports[materia.id]?.classes.map(cls => {
                                                           const report = attendanceReports[materia.id];
                                                           const presents = report.students.filter(s => s.attendance[cls.id]).length;
                                                           const absents = cls.is_future ? 0 : report.students.filter(s => !s.attendance[cls.id]).length;
                                                           return (
                                                             <th key={cls.id} className="text-center" style={{ minWidth: '75px' }}>
                                                               <div className="d-flex flex-column align-items-center">
                                                                 <div className="d-flex gap-1 mb-1">
                                                                   <span className="badge bg-success py-0 px-1" style={{ fontSize: '0.6rem' }} title="Presentes">P: {presents}</span>
                                                                   {!cls.is_future && <span className="badge bg-danger py-0 px-1" style={{ fontSize: '0.6rem' }} title="Ausentes">A: {absents}</span>}
                                                                 </div>
                                                                 <div className={`${cls.is_future ? 'text-info opacity-75' : 'text-dark fw-bold'}`}>
                                                                   {cls.date}
                                                                   {cls.duration != null && <div className="fw-normal text-muted" style={{ fontSize: '0.65rem' }}>{cls.duration} hs</div>}
                                                                   {cls.is_future && <div className="fw-normal text-muted" style={{ fontSize: '0.65rem' }}>(Futura)</div>}
                                                                 </div>
                                                               </div>
                                                             </th>
                                                           );
                                                         })}
                                                       </tr>
                                                     </thead>
                                                     <tbody>
                                                       {attendanceReports[materia.id]?.students && attendanceReports[materia.id].students
                                                         .filter(s => {
                                                           const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                           const studentEmail = (s.email || '').toLowerCase().trim();
                                                           const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                           return matchesSearch && isSelf;
                                                         }).length > 0 ? 
                                                         attendanceReports[materia.id].students
                                                         .filter(s => {
                                                           const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase());
                                                           const studentEmail = (s.email || '').toLowerCase().trim();
                                                           const isSelf = activeRole === 'AL' ? studentEmail === userEmail : true;
                                                           return matchesSearch && isSelf;
                                                         })
                                                         .map(s => (
                                                           <tr key={s.id}>
                                                             <td className="fw-bold sticky-column bg-white" style={{ position: 'sticky', left: 0 }}>{s.full_name}</td>
                                                             {attendanceReports[materia.id]?.classes.map(cls => (
                                                               <td key={cls.id} className="text-center p-2">
                                                                 {s.attendance[cls.id] ? (
                                                                   <div className="badge bg-success rounded-circle p-1" style={{ width: '20px', height: '20px' }}>✓</div>
                                                                 ) : (
                                                                   <div className={`badge ${cls.is_future ? 'bg-light text-muted border' : 'bg-danger'} rounded-circle p-1`} style={{ width: '20px', height: '20px' }}>
                                                                     {cls.is_future ? ' ' : '✗'}
                                                                   </div>
                                                                 )}
                                                                 {!!s.connected_minutes?.[cls.id] && (
                                                                   <div className="text-muted" style={{ fontSize: '1.17rem' }}>{s.connected_minutes[cls.id]}m</div>
                                                                 )}
                                                               </td>
                                                             ))}
                                                           </tr>
                                                         )) : (
                                                         <tr>
                                                           <td colSpan={(attendanceReports[materia.id]?.classes.length || 0) + 1} className="text-center p-4 text-muted fst-italic">
                                                              No hay datos de asistencia para mostrar.
                                                           </td>
                                                         </tr>
                                                       )}
                                                     </tbody>
                                                   </table>
                                                 </div>
                                               </div>
                                             )}
                                            {/* VISTA 4: PROFESORES */}
                                            {activeSubmenus[materia.id] === 'profesores' && (
                                              <div className="table-responsive">
                                                <table className="table table-sm table-hover premium-table align-middle mb-0" style={{ fontSize: '0.85rem' }}>
                                                  <thead className="table-light text-muted">
                                                    <tr>
                                                      <th>Nombre</th>
                                                      <th>Email</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {materia.teachers_data && materia.teachers_data.length > 0 ? materia.teachers_data.map(teacher => (
                                                      <tr key={teacher.id}>
                                                        <td className="fw-bold text-dark">{teacher.first_name} {teacher.last_name}</td>
                                                        <td className="text-muted">{teacher.email}</td>
                                                      </tr>
                                                    )) : (
                                                      <tr><td colSpan="2" className="text-center p-3 text-muted fst-italic">No hay profesores asignados.</td></tr>
                                                    )}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}

                                            {/* VISTA 5: COORDINADOR */}
                                            {activeSubmenus[materia.id] === 'coordinador' && (
                                              <div className="p-3 bg-light rounded-3 border">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                  <div className="d-flex align-items-center">
                                                    <UserPlus size={20} className="text-primary me-2" />
                                                    <span className="fw-bold">Coordinador de Diplomatura:</span>
                                                  </div>
                                                  {activeRole === 'ACO' && (
                                                    <button 
                                                      className="btn btn-sm btn-outline-primary border-0 p-1"
                                                      onClick={() => setEditDiplo(diplo)}
                                                      title="Editar Coordinador / Diplomatura"
                                                    >
                                                      <Pencil size={16} />
                                                    </button>
                                                  )}
                                                </div>
                                                <div className="ps-4">
                                                  <div className="fw-bold fs-5 text-dark mb-1">{diplo.coordinator_name || 'Sin asignar'}</div>
                                                  <div className="text-muted small d-flex align-items-center">
                                                    <Mail size={14} className="me-1" /> {diplo.coordinator_email || 'N/A'}
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-muted small ps-3 fst-italic">Esta diplomatura aún no tiene materias creadas.</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {!loading && diplomaturas.length === 0 && (
                <div className="text-center text-muted p-4 border border-dashed rounded bg-light">
                  <BookOpen size={32} className="mb-2 opacity-50" />
                  <br />
                  <em>{activeRole === 'CD' ? 'No tienes diplomaturas asignadas en tus Datos Personales.' : activeRole === 'RM' ? 'No tienes diplomaturas asignadas como referente municipal.' : 'No hay diplomaturas habilitadas en este momento.'}</em>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer border-0 pt-0">
            <button type="button" className="btn btn-secondary w-100" onClick={onClose}>
              <X size={16} className="me-1" /> Cerrar Vista
            </button>
          </div>
        </div>
      </div>
      {editDiplo && (
        <CreateDiplomaturaMenu 
          editData={editDiplo} 
          onClose={() => { setEditDiplo(null); fetchDiplomaturas(); }} 
        />
      )}
      {editMateria && (
        <CreateMateriaMenu 
          editData={editMateria} 
          onClose={() => { setEditMateria(null); fetchDiplomaturas(); }} 
        />
      )}
      {showEnrollmentModal && (
        <AuthorityEnrollmentModal 
          isOpen={showEnrollmentModal}
          onClose={() => {
            setShowEnrollmentModal(false);
            setSelectedDiploForEnroll(null);
          }}
          initialDiploma={selectedDiploForEnroll?.id}
          onEnrollSuccess={fetchDiplomaturas}
        />
      )}
      {/* MODAL DE CRITERIO PARA ALUMNOS VIGENTES */}
      {showActiveStudentsModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '420px' }}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 bg-success bg-opacity-10 text-success p-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 bg-success bg-opacity-20 rounded-circle">
                    <Download size={22} />
                  </div>
                  <div>
                    <h5 className="modal-title fw-bold mb-0">Alumnos Vigentes</h5>
                    <small className="opacity-75">{selectedDiploForActiveStudents?.name}</small>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowActiveStudentsModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <label className="form-label small fw-bold text-muted text-uppercase">Criterio: % de asistencia mínimo</label>
                <select
                  className="form-select mb-2"
                  value={activeStudentsThreshold}
                  onChange={(e) => setActiveStudentsThreshold(Number(e.target.value))}
                >
                  {[50, 60, 70, 80, 90].map(p => (
                    <option key={p} value={p}>≥ {p}%</option>
                  ))}
                </select>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">Otro %</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="form-control"
                    value={activeStudentsThreshold}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isNaN(v)) return;
                      setActiveStudentsThreshold(Math.min(100, Math.max(0, v)));
                    }}
                  />
                </div>
                <p className="text-muted small mt-3 mb-0">
                  Se van a incluir los alumnos cuya asistencia acumulada en la diplomatura sea mayor o igual a <strong>{activeStudentsThreshold}%</strong>.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0 p-4">
                <button type="button" className="btn btn-light rounded-pill" onClick={() => setShowActiveStudentsModal(false)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success rounded-pill d-flex align-items-center gap-2"
                  disabled={isDownloadingActiveStudents}
                  onClick={handleDownloadActiveStudents}
                >
                  {isDownloadingActiveStudents ? (
                    <span className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <Download size={16} />
                  )}
                  Descargar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* MODAL DE SOLICITUD DE CONSTANCIA */}
      {showCertificateRequestModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-scrollable my-3 mx-auto" style={{ maxWidth: '500px' }}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 bg-success bg-opacity-10 text-success p-4">
                <div className="d-flex align-items-center gap-3">
                  <div className="p-2 bg-success bg-opacity-20 rounded-circle">
                    <FileText size={24} />
                  </div>
                  <div>
                    <h5 className="modal-title fw-bold mb-0">Solicitar Constancia</h5>
                    <small className="opacity-75">{selectedDiploForCertificate?.name}</small>
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowCertificateRequestModal(false)}></button>
              </div>

              {/* PASO 1: Selección de tipo */}
              {!certType && (() => {
                const existingCustom = myCustomCertRequests.find(
                  // eslint-disable-next-line eqeqeq
                  r => r.diploma == selectedDiploForCertificate?.id
                );
                return (
                  <div key="cert-step-select" className="modal-body p-4">
                    <p className="text-muted small mb-4">¿Qué tipo de constancia necesitás?</p>
                    <div className="d-flex flex-column gap-3">
                      <button
                        className="btn btn-outline-success text-start p-4 rounded-4 d-flex align-items-start gap-3 border-2"
                        onClick={() => setCertType('regular')}
                        style={{ transition: 'all 0.15s' }}
                      >
                        <div className="p-2 bg-success bg-opacity-10 rounded-circle flex-shrink-0">
                          <CheckCircle size={22} className="text-success" />
                        </div>
                        <div>
                          <div className="fw-bold">Constancia de Alumno Regular</div>
                          <div className="small text-muted mt-1">Certifica que sos alumno/a regular de la diplomatura. Texto estándar emitido por el programa.</div>
                        </div>
                      </button>

                      {/* Constancia Personalizada — con estado si ya existe */}
                      {existingCustom ? (
                        <div className="p-4 rounded-4 border-2 border d-flex align-items-start gap-3"
                          style={{ backgroundColor: existingCustom.status === 'EM' ? '#f0fdf4' : '#fffbeb', borderColor: existingCustom.status === 'EM' ? '#86efac' : '#fcd34d' }}>
                          <div className={`p-2 rounded-circle flex-shrink-0 ${existingCustom.status === 'EM' ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'}`}>
                            {existingCustom.status === 'EM'
                              ? <CheckCircle size={22} className="text-success" />
                              : <Clock size={22} className="text-warning" />}
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-bold">Constancia Personalizada</div>
                            {existingCustom.status === 'EM' && (
                              <>
                                <div className="small text-success mt-1 fw-semibold">Tu constancia fue emitida y está lista.</div>
                                <button
                                  className="btn btn-success btn-sm rounded-pill px-3 mt-2 d-flex align-items-center gap-2"
                                  onClick={() => { setCustomCertToView(existingCustom); setShowCustomCertView(true); }}
                                >
                                  <Printer size={14} /> Ver e Imprimir
                                </button>
                              </>
                            )}
                            {existingCustom.status === 'PE' && (
                              <div className="small text-warning-emphasis mt-1">Tu solicitud está siendo revisada por el coordinador de la diplomatura.</div>
                            )}
                            {existingCustom.status === 'VC' && (
                              <div className="small text-info-emphasis mt-1">El coordinador la aprobó. El equipo de coordinación está redactando tu constancia.</div>
                            )}
                            {existingCustom.status === 'RE' && (
                              <>
                                <div className="small text-danger mt-1 fw-semibold">Solicitud rechazada.</div>
                                {existingCustom.rejection_reason && <div className="x-small text-muted mt-1">Motivo: {existingCustom.rejection_reason}</div>}
                                <button className="btn btn-outline-primary btn-sm rounded-pill px-3 mt-2"
                                  onClick={() => setCertType('personalizada')}>
                                  Hacer una nueva solicitud
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button
                          className="btn btn-outline-primary text-start p-4 rounded-4 d-flex align-items-start gap-3 border-2"
                          onClick={() => setCertType('personalizada')}
                          style={{ transition: 'all 0.15s' }}
                        >
                          <div className="p-2 bg-primary bg-opacity-10 rounded-circle flex-shrink-0">
                            <FileText size={22} className="text-primary" />
                          </div>
                          <div>
                            <div className="fw-bold">Constancia Personalizada</div>
                            <div className="small text-muted mt-1">Para otros propósitos. Describís brevemente qué necesitás y el equipo de coordinación redacta el texto a medida.</div>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* PASO 2A: Constancia Regular */}
              {certType === 'regular' && (
                <React.Fragment key="cert-step-regular">
                  <div className="modal-body p-4">
                    {profileNameMissing && (
                      <div className="alert alert-danger border-0 rounded-4 d-flex align-items-start gap-3 p-3 mb-4">
                        <AlertCircle size={20} className="flex-shrink-0 mt-1" />
                        <div className="small">
                          <strong>Datos de perfil incompletos.</strong><br />
                          Tu nombre y/o apellido no están cargados. Completalos en <strong>Datos Personales</strong> antes de continuar.
                        </div>
                      </div>
                    )}
                    <div className="mb-4">
                      <label className="form-label fw-bold small text-muted text-uppercase mb-2">Confirmar Municipio</label>
                      <div className="position-relative">
                        <MapPin className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={18} />
                        <input
                          type="text"
                          className="form-control ps-5 py-3 border-0 bg-light shadow-sm"
                          placeholder="Ej: Mercedes, Coronel Rosales..."
                          value={requestMunicipality}
                          onChange={(e) => setRequestMunicipality(e.target.value)}
                          style={{ borderRadius: '12px' }}
                        />
                      </div>
                      <small className="text-muted mt-2 d-block">Este municipio aparecerá impreso en la constancia.</small>
                    </div>
                    <div className="alert alert-info border-0 bg-info bg-opacity-10 d-flex gap-3 p-3 rounded-4">
                      <BarChart2 size={30} className="text-info flex-shrink-0" />
                      <div className="small text-info-emphasis">
                        Tu solicitud será enviada al equipo de coordinación para su validación.
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-0 p-4 pt-0 d-flex gap-2">
                    <button className="btn btn-light py-2 px-3 rounded-pill fw-bold" onClick={() => setCertType(null)}>
                      ← Volver
                    </button>
                    <button
                      className="btn btn-success py-2 px-4 rounded-pill fw-bold d-flex align-items-center gap-2 shadow-sm ms-auto"
                      onClick={handleRequestCertificate}
                      disabled={isSubmittingRequest || !requestMunicipality || !!profileNameMissing}
                    >
                      {isSubmittingRequest ? <span className="spinner-border spinner-border-sm"></span> : <FileText size={18} />}
                      {isSubmittingRequest ? "Enviando..." : "Confirmar Pedido"}
                    </button>
                  </div>
                </React.Fragment>
              )}

              {/* PASO 2B: Constancia Personalizada */}
              {certType === 'personalizada' && (
                <React.Fragment key="cert-step-personalizada">
                  <div className="modal-body p-4">
                    <p className="text-muted small mb-3">
                      Describí brevemente para qué necesitás la constancia. El equipo de coordinación la va a redactar y emitir a medida.
                    </p>
                    <div className="mb-3">
                      <label className="form-label fw-bold small text-muted text-uppercase mb-2">¿Para qué la necesitás? <span className="text-danger">*</span></label>
                      <textarea
                        className="form-control border-0 bg-light shadow-sm"
                        rows={4}
                        placeholder="Ej: para presentar ante mi empleador como justificativo de capacitación, para tramitar una beca, para un trámite bancario..."
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        maxLength={500}
                        style={{ borderRadius: '12px' }}
                        autoFocus
                      />
                      <small className="text-muted mt-1 d-block text-end">{customDescription.length}/500</small>
                    </div>
                    <div className="alert alert-primary border-0 bg-primary bg-opacity-10 d-flex gap-3 p-3 rounded-4">
                      <FileText size={24} className="text-primary flex-shrink-0 mt-1" />
                      <div className="small text-primary-emphasis">
                        Una vez enviada tu solicitud, el Auxiliar de Coordinación Operativa redactará el texto y emitirá la constancia personalizada.
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-0 p-4 pt-0 d-flex gap-2">
                    <button className="btn btn-light py-2 px-3 rounded-pill fw-bold" onClick={() => setCertType(null)}>
                      ← Volver
                    </button>
                    <button
                      className="btn btn-primary py-2 px-4 rounded-pill fw-bold d-flex align-items-center gap-2 shadow-sm ms-auto"
                      onClick={handleRequestCustomCertificate}
                      disabled={isSubmittingCustom || !customDescription.trim()}
                    >
                      {isSubmittingCustom ? <span className="spinner-border spinner-border-sm"></span> : <FileText size={18} />}
                      {isSubmittingCustom ? "Enviando..." : "Enviar Solicitud"}
                    </button>
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Vista / impresión de constancia personalizada emitida (alumno) */}
    {showCustomCertView && customCertToView && (
      <CustomCertPrintModal
        title={customCertToView.certificate_title}
        text={customCertToView.aco_custom_text}
        studentName={customCertToView.student_name}
        studentDni={customCertToView.student_dni}
        diplomaName={customCertToView.diploma_name}
        municipality={customCertToView.municipality}
        onClose={() => { setShowCustomCertView(false); setCustomCertToView(null); }}
      />
    )}
    </>
  );
};

export default DiplomaturasListModal;
